import { computed, inject } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { tapResponse } from '@ngrx/operators';
import { patchState, signalStore, withComputed, withMethods, withProps, withState, } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { pipe, switchMap } from 'rxjs';
import { UserInfo } from '../models/user-info';
import { DamageRoles } from '../models/roles';
import { UserService } from '../services/user.service';
import { BranchMappingService } from '../../services/branch-mapping.service';


export interface UserState {
  user: UserInfo | null;
  branchMappingsLoaded: boolean;
}

const initialState: UserState = {
  user: null,
  branchMappingsLoaded: false,
};

/** Turn a raw role_id like "system_admin" into a pretty label like "System Admin". */
function prettifyRole(role: string): string {
  return role
    .split('_')
    .filter(Boolean)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

export const UserStore = signalStore(
  {providedIn: 'root'},
  withState(initialState),
  withMethods((store,
               userService = inject(UserService),
               branchMappingService = inject(BranchMappingService)) => ({
    updateBranchSelections: (displayBranches: string[], notificationBranches: string[]) => {
      const current = store.user();
      if (current) {
        patchState(store, { user: {
          ...current, dmg_branches: displayBranches, selected_branches: displayBranches, notification_branches: notificationBranches,
        } });
        branchMappingService.initializeBranchMappingsForUser(displayBranches).subscribe();
        userService.loadUserBranches().subscribe({
          next: (branches) => {
            const latest = store.user();
            if (latest && branches.length) patchState(store, { user: { ...latest, dmg_branches: branches } });
          },
          error: () => console.error('Error refreshing user branches'),
        });
      }
    },
    refreshBranches: rxMethod<void>(
      pipe(
        switchMap(() => userService.loadUserBranches().pipe(
          tapResponse({
            next: (branches) => {
              const current = store.user();
              // The endpoint answers [] when its own lookup fails, so an empty list is not applied.
              if (current && branches.length) {
                patchState(store, { user: { ...current, dmg_branches: branches } });
              }
            },
            error: () => {
              console.error('Error refreshing user branches');
            },
          }),
        )),
      )
    ),
    loadUserInfo: rxMethod<void>(
      pipe(
        switchMap(() => {
          return userService.loadUserInfo().pipe(
            switchMap((user) => {
              // Initialize branch mappings and wait for completion
              const userBranches = [...new Set((user?.selected_branches ?? user?.dmg_branches)?.filter(branch => !!branch) || [])];
              return branchMappingService.initializeBranchMappingsForUser(userBranches).pipe(
                tapResponse({
                  next: (branchDetails) => {
                    patchState(store, (state) => ({
                      user: state.user
                        ? { ...state.user, dmg_branch_details: branchDetails }
                        : { ...user, dmg_branch_details: branchDetails },
                      branchMappingsLoaded: true,
                    }));
                  },
                  error: (error) => {
                    console.error('Error initializing branch mappings:', error);
                    // Still mark as loaded even if error, so UI doesn't hang
                    patchState(store, { user, branchMappingsLoaded: true });
                  },
                })
              );
            }),
            tapResponse({
              next: () => {
                // Already handled in switchMap above
              },
              error: () => {
                console.error('Error getting user info');
              },
            }),
          );
        })
      )
    ),
  })),
  withProps(({user}) => ({
    user$: toObservable(user),
  })),
  withComputed(({user, branchMappingsLoaded}) => ({
    userName: computed(() => {
      return user()
        ? `${user()?.given_name} ${user()?.family_name}`
        : '';
    }),
    branches: computed(() => {
      return user()?.dmg_branches?.filter(branch => !!branch) || [];
    }),
    selectedBranches: computed(() => {
      return (user()?.selected_branches ?? user()?.dmg_branches)?.filter(branch => !!branch) || [];
    }),
    branchLimit: computed(() => user()?.branch_limit ?? null),
    notificationBranches: computed(() => {
      return user()?.notification_branches ?? [];
    }),
    isReady: computed(() => {
      return !!user() && branchMappingsLoaded();
    }),
    branchOptions: computed(() => {
      return user()?.dmg_branch_details?.map(branch => ({
        value: branch.branch_number,
        label: branch.branch_name?.trim() ? `${branch.branch_number} - ${branch.branch_name}` : branch.branch_number
      })) || [];
    }),
    isUnlimited: computed(() => {
      return user()?.branch_limit === null;
    }),

     // ── LEGACY path — grp_OKTA_DMG_* groups (DELETE after migration cutover) ─
    /** @deprecated Use effectiveRoles after migration */
    roles: computed(() => {
      const dict = user()?.dmg_groups_dictionary ?? {};
      return user()?.dmg_groups.map(role => dict[role] || prettifyRole(role)) || [];
    }),
    // ── Dual-path signals — work for BOTH old and new users simultaneously ───
    //    Rule: if effective_permissions is non-empty → user is on new system.
    //    Otherwise fall back to legacy dmg_permissions / dmg_groups.

    /** True when the user has been migrated to the new RH_*_Prod Okta groups */
    isNewPermissionSystem: computed(() => {
      return (user()?.effective_roles?.length ?? 0) > 0;
    }),

    /**
     * The resolved permissions for the current user.
     * New-system users: effective_permissions from role_definition DynamoDB table.
     * Legacy users: dmg_permissions from GroupPermissionMapping DynamoDB row.
     */
    resolvedPermissions: computed(() => {
      const ep = user()?.effective_permissions;
      return (ep && ep.length > 0) ? ep : (user()?.dmg_permissions ?? []);
    }),

    /**
     * The resolved role IDs for the current user.
     * New-system users: effective_roles (e.g. 'tier_1_approver').
     * Legacy users: dmg_groups short names (e.g. 'bm').
     */
    resolvedRoles: computed(() => {
      const er = user()?.effective_roles;
      return (er && er.length > 0) ? er : (user()?.dmg_groups ?? []);
    }),

    // ── NEW path signals — populated only for migrated users ─────────────────

    /** Role IDs from the new system (e.g. 'tier_1_approver'). Empty for legacy users. */
    effectiveRoles: computed(() => user()?.effective_roles ?? []),

    /** True when Read Only is the user's only role; such users can view but never change data. */
    isReadOnlyOnly: computed(() => {
      const roles = user()?.effective_roles ?? [];
      return roles.length > 0 && roles.every(role => role === DamageRoles.READ_ONLY);
    }),

    /** Permissions from role_definition table. Empty for legacy users. */
    effectivePermissions: computed(() => user()?.effective_permissions ?? []),

    /** True when the user's role is not capped to specific branches */
    isGlobal: computed(() => user()?.is_global ?? false),

    /** True when the user's GHR job title maps to a different role than their Okta group */
    roleMismatch: computed(() => user()?.role_mismatch ?? false),

    /** Human-readable mismatch warning messages */
    mismatchWarnings: computed(() => user()?.warnings ?? []),

    /** Raw GHR job title from OTG (e.g. 'Branch Manager US') */
    ghrTitle: computed(() => user()?.ghr_title ?? ''),

    /** Role ID that the user's job title maps to (e.g. 'tier_1_approver') */
    expectedRole: computed(() => user()?.expected_role ?? ''),

    /** Permissions removed for this user by their GHR title override (subtractive only) */
    titlePermissionRestrictions: computed(() => user()?.title_permission_restrictions ?? []),
  }))
);
